import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { catchError, Observable, throwError } from 'rxjs';
import { AuthService } from 'app/core/auth/auth.service';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { Router } from '@angular/router';
import { StorageService } from '../services/storage/storage.service';
import { NotificationService } from '../services/notification/notification.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  /** Timestamp of the last "permission denied" toast, to avoid stacking
   *  duplicate notifications when several 403 responses arrive at once. */
  private _lastForbiddenToastAt = 0;

  constructor(
    private _authService: AuthService,
    private _router: Router,
    private _storageService: StorageService,
    private _notificationService: NotificationService
  ) {}

  /**
   * Intercept
   *
   * @param req
   * @param next
   */
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Clone the request object
    let newReq = req.clone();

    // Request
    //
    // If the access token didn't expire, add the Authorization header.
    // We won't add the Authorization header if the access token expired.
    // This will force the server to return a "401 Unauthorized" response
    // for the protected API routes which our response interceptor will
    // catch and delete the access token from the local storage while logging
    // the user out from the app.
    if (
      this._storageService.getItem('primecontrol_accessToken') &&
      !AuthUtils.isTokenExpired(this._storageService.getItem('primecontrol_accessToken'))
    ) {
      newReq = req.clone({
        headers: req.headers.set(
          'Authorization',
          'Bearer ' + this._storageService.getItem('primecontrol_accessToken')
        ),
      });
    }

    // Response
    return next.handle(newReq).pipe(
      catchError((error) => {
        if (error instanceof HttpErrorResponse) {
          // Catch "401 Unauthorized" responses
          if (error.status === 401) {
            // Sign out
            this._authService.signOut();

            this._router.navigate(['auth/sign-in']);
          }

          // Catch "403 Forbidden" responses: the user is authenticated but
          // lacks permission for this action — surface a toast instead of
          // failing silently in the console.
          if (error.status === 403) {
            const now = Date.now();
            if (now - this._lastForbiddenToastAt > 2000) {
              this._lastForbiddenToastAt = now;
              this._notificationService
                .notifications({
                  type: 'error',
                  title: 'ACCESS_DENIED',
                  message: 'FORBIDDEN_ACTION_MESSAGE',
                })
                .subscribe();
            }
          }

          // Everything else gets logged, not toasted. 152 call sites already
          // report their own failure ("ERROR_UPDATING_CUSTOMER" and friends), so a
          // blanket toast here would double up on all of them; and a call site
          // that DOES handle the error stops the browser's unhandled-rejection
          // log, which is the only trace support had. One line per failure, with
          // the method and URL, keeps that trace whoever handles the error.
          if (error.status !== 401 && error.status !== 403) {
            console.error(
              `[http] ${req.method} ${req.urlWithParams} → ${error.status} ${error.statusText}`,
              error.error
            );
          }
        }

        return throwError(() => error);
      })
    );
  }
}
